import { queryOptions, useQuery } from "@tanstack/react-query";

import { fetchResidenceCallScriptHistoryMock } from "./callScriptResidence.mock";
import type { ResidenceCallScriptHistory } from "./callScriptResidence.types";
import { callScriptResidenceKeys } from "./callScriptResidenceKeys";

/**
 * Shared queryOptions for an applicant's saved residence ("Customer
 * Verification") Call Script history (key + queryFn). The employment
 * equivalent is `callScriptHistoryQuery`.
 *
 * Disabled until both ids are present and the caller opts in — the modal
 * enables it only while open, so closing the popup stops it refetching.
 *
 * TODO(BFF): served by `fetchResidenceCallScriptHistoryMock` until the
 * endpoint exists. The generated client (1.0.780) has no residence
 * verification API; once it does, this queryFn becomes
 * `mapResidenceCallScriptHistory((await api.getResidenceHistory(approvalId, applicantId, { signal })).data)`
 * and callScriptResidence.mock.ts can be deleted.
 * @param {string} approvalId - The deal / approval identifier (path param).
 * @param {string} applicantId - The applicant identifier (path param).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guards.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function callScriptResidenceHistoryQuery(approvalId: string, applicantId: string, enabled = true) {
  return queryOptions({
    queryKey: callScriptResidenceKeys.history({ approvalId, applicantId }),
    queryFn: async ({ signal }): Promise<ResidenceCallScriptHistory> =>
      fetchResidenceCallScriptHistoryMock(approvalId, applicantId, signal),
    staleTime: 30_000,
    retry: false,
    enabled: enabled && !!approvalId && !!applicantId,
  });
}

/**
 * Reads an applicant's saved residence Call Script history. Disabled until
 * the approval id and applicant id are present and the caller enables it.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} applicantId - The applicant identifier.
 * @param {{ enabled?: boolean }} [options] - Caller opt-in (defaults to enabled).
 * @param {boolean} [options.enabled] - When false, the query never fires.
 * @returns {ReturnType<typeof useQuery<ResidenceCallScriptHistory>>} The TanStack Query result.
 */
export function useCallScriptResidenceHistory(
  approvalId: string,
  applicantId: string,
  options?: { enabled?: boolean }
) {
  return useQuery(callScriptResidenceHistoryQuery(approvalId, applicantId, options?.enabled ?? true));
}
